import Timeline from '@mui/lab/Timeline';
import TimelineItem from 'components/TimelineItem';
import { timelineItemClasses } from '@mui/lab/TimelineItem';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import IssueDetailsLoader from 'views/IssueDetails/components/Loaders/IssueDetailsLoader';
import { InfoLabel } from 'views/IssueDetails/components/Info/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import { Tooltip } from '@mui/material';
import AILoader from 'views/IssueDetails/components/Loaders/AILoader';
import Info from 'views/IssueDetails/components/Info';
import Button from 'components/Button';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { USER_DATA, LANGUAGE } from 'shared/constants/users';
import logger from 'utils/logger';
import { zIndexValues } from 'utils/styles';
import { useAIAnalytics } from '../hooks/useAIAnalytics';
import {
  AIDescription,
  DescriptionLink,
  IssueDetailsContainer,
  Left,
  Right,
  TimelineWrapper,
} from 'views/IssueDetails/components/AIAnalysis/Styles';

export const FETCH_STATUS = {
  STARTED: 'started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
};

const propTypes = {
  analyticData: PropTypes.object.isRequired,
  fetchData: PropTypes.func.isRequired,
  issue: PropTypes.object.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  rerunDataAnalysis: PropTypes.func.isRequired,
};

const log = logger.createLogger('AIFileDescriptionLayout');

/**
 * AI File Description Layout - Original comprehensive layout
 *
 * This layout provides the full AI file analysis experience with:
 * - Left panel: Issue info and AI summary with clickable file references
 * - Right panel: Timeline of file attachments with sorting functionality
 * - Interactive features: File reference links, sorting, scrolling to sections
 */
const AIFileDescriptionLayout = ({
  analyticData,
  fetchData,
  fetchStatus,
  issue,
  rerunDataAnalysis,
}) => {
  const [activeSection, setActiveSection] = useState(null);
  const [isSortedNewest, setIsSortedNewest] = useState(false);
  const [sortedAttachments, setSortedAttachments] = useState([]);

  // Use the centralized hook instead of duplicate logic
  const { attachments, startingId, isLoading, isInProgress } = useAIAnalytics({
    analyticData,
    fetchData,
    fetchStatus,
    rerunDataAnalysis,
  });

  // Update sorted attachments when attachments change
  useEffect(() => {
    setSortedAttachments(attachments);
  }, [attachments]);

  const addDescriptionLinks = (description, attachmentsAnalytics) => {
    // Function to find matching closing reference tag
    const findClosingRef = (str, startIndex) => {
      const endTag = 'REF_END';
      const endTagIndex = str.indexOf(endTag, startIndex);
      return endTagIndex !== -1 ? endTagIndex + endTag.length - 1 : -1;
    };

    // Function to check if string has a file extension
    const hasFileExtension = (str) => {
      return /\.[a-zA-Z0-9]+$/.test(str);
    };

    const elements = [];
    let currentIndex = 0;

    while (currentIndex < description.length) {
      const openRefIndex = description.indexOf('REF_START', currentIndex);
      if (openRefIndex === -1) {
        // Split the remaining text by newlines and join with br tags
        const remainingText = description.slice(currentIndex);
        const textWithLineBreaks = remainingText
          .split('\n')
          .map((text, index, array) => (
            <Fragment key={`text-${index}`}>
              {text}
              {index < array.length - 1 && <br />}
            </Fragment>
          ));
        elements.push(textWithLineBreaks);
        break;
      }

      // Handle text before the reference tag
      const textBeforeRef = description.slice(currentIndex, openRefIndex);
      const textWithLineBreaks = textBeforeRef
        .split('\n')
        .map((text, index, array) => (
          <Fragment key={`text-${index}`}>
            {text}
            {index < array.length - 1 && <br />}
          </Fragment>
        ));
      elements.push(textWithLineBreaks);

      const startTagEnd = openRefIndex + 'REF_START'.length;
      const closeRefIndex = findClosingRef(description, startTagEnd);

      if (closeRefIndex === -1) {
        elements.push(description.slice(currentIndex));
        break;
      }

      const filesText = description.slice(
        startTagEnd,
        closeRefIndex - 'REF_END'.length + 1
      );
      const files = filesText
        .split('|')
        .map((file) => file.trim())
        .filter((file) => file.length > 0);

      elements.push('[');

      files.forEach((file, fileIndex) => {
        if (fileIndex > 0) {
          elements.push(', ');
        }

        if (hasFileExtension(file)) {
          const foundItem = (attachmentsAnalytics || []).find(
            (item) => item.filename === file
          );
          const itemNumber = foundItem?.id ? foundItem.id - startingId : '?';

          elements.push(
            <Tooltip key={`tooltip-${openRefIndex}-${fileIndex}`} title={file}>
              <DescriptionLink onClick={() => scrollToSection(file)}>
                {itemNumber}
              </DescriptionLink>
            </Tooltip>
          );
        } else {
          elements.push(file);
        }
      });

      elements.push(']');

      currentIndex = closeRefIndex + 1;
    }

    return <AIDescription>{elements}</AIDescription>;
  };

  const sortAttachments = () => {
    const newSortedAttachments = [...attachments].sort((a, b) => {
      return isSortedNewest
        ? new Date(b.resultMetaData.creationDate_accepted).getTime() -
            new Date(a.resultMetaData.creationDate_accepted).getTime()
        : new Date(a.resultMetaData.creationDate_accepted).getTime() -
            new Date(b.resultMetaData.creationDate_accepted).getTime();
    });
    setSortedAttachments(newSortedAttachments);
    setIsSortedNewest(!isSortedNewest);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
      setActiveSection(sectionId);
      section.classList.add('active');

      setTimeout(() => {
        section.classList.remove('active');
      }, 2000);
    }
  };

  /**
   * Parses a date-time string and formats it according to user's language preference
   *
   * @param {string} dateTime - ISO date-time string
   * @returns {string} Formatted date string
   */
  const parseDateTime = (dateTime) => {
    let formattedDateTime;
    const parsedDateTime = new Date(dateTime);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    if (
      localStorage.getItem(USER_DATA.PREFFERED_LANGUAGE) === LANGUAGE.JAPANSESE
    ) {
      formattedDateTime = parsedDateTime.toLocaleString('ja-JP', options);
    } else {
      formattedDateTime = parsedDateTime.toLocaleString('en-US', options);
    }
    return formattedDateTime;
  };

  useEffect(() => {
    if (activeSection) {
      scrollToSection(activeSection);
    }
  }, [activeSection]);

  // show loading state
  if (isLoading || isInProgress) {
    return (
      <Fragment>
        <IssueDetailsContainer container>
          <Grid
            size={12}
            style={{ position: 'relative', marginTop: 20, marginBottom: 20 }}>
            <AILoader />
            <IssueDetailsLoader />
            <IssueDetailsLoader />
          </Grid>
        </IssueDetailsContainer>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <IssueDetailsContainer container>
        <Left size={8}>
          <Info issue={issue} />
          <Divider />

          {/* Header Section with Rerun Button */}
          <Grid container spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
            <Grid size={2}>
              <InfoLabel>
                {intl.get('issue_details_info_label_ai_summary')}
              </InfoLabel>
            </Grid>
            <Grid
              size={10}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={rerunDataAnalysis}
                icon={<RefreshIcon />}
                disabled={isInProgress}
                sx={{ minWidth: 'auto' }}>
                Rerun Analysis
              </Button>
            </Grid>
          </Grid>

          {/* AI Summary Content */}
          <Grid container spacing={1}>
            <Grid size={2}>{/* Empty space to align with header */}</Grid>
            <Grid size={10}>
              {analyticData?.data?.issueAnalytics?.result?.answer
                ? addDescriptionLinks(
                    analyticData.data.issueAnalytics.result.answer,
                    attachments || []
                  )
                : intl.get('ai_feature_text_no_ai_summary')}
            </Grid>
          </Grid>
        </Left>
        <Right size={4}>
          <Stack>
            <Grid
              container
              justifyContent="space-between"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: zIndexValues.modal4 + 1,
                backgroundColor: '#FFF',
                padding: '16px 0',
                borderColor: 'divider',
              }}>
              <InfoLabel>
                {intl.get('issue_details_info_label_file_timeline')}
              </InfoLabel>
              <Button
                variant="primary"
                sx={{ justifyContent: 'start' }}
                onClick={sortAttachments}
                icon={<SwapVertIcon />}>
                {isSortedNewest
                  ? intl.get('issue_details_button_sort_by_newest')
                  : intl.get('issue_details_button_sort_by_oldest')}
              </Button>
            </Grid>
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}>
              {sortedAttachments.map((item, _) => {
                const { filename, filetype, resultMetaData, result, id } = item;
                log.debug(
                  'timeline item filename',
                  filename,
                  'resultMetaData',
                  resultMetaData?.patientName_learnt
                );

                // Handle different result.answer formats
                let description = intl.get('ai_feature_text_no_description');
                if (result?.answer) {
                  if (typeof result.answer === 'string') {
                    description = result.answer;
                  } else if (
                    typeof result.answer === 'object' &&
                    result.answer.original_comment
                  ) {
                    description = result.answer.original_comment;
                  }
                }

                return (
                  <TimelineWrapper
                    id={`${filename}`}
                    key={id}
                    className={activeSection === filename ? 'active' : ''}>
                    <TimelineItem
                      index={startingId !== null ? id - startingId : id}
                      extension={filetype}
                      title={filename}
                      dateTime={parseDateTime(
                        resultMetaData?.creationDate_accepted
                      )}
                      description={description}
                      isBadgeVisible={!resultMetaData?.patientName_matches}
                      patientNameLearnt={resultMetaData?.patientName_learnt}
                    />
                  </TimelineWrapper>
                );
              })}
            </Timeline>
          </Stack>
        </Right>
      </IssueDetailsContainer>
    </Fragment>
  );
};

AIFileDescriptionLayout.propTypes = propTypes;

export default AIFileDescriptionLayout;
